import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize Gemini API
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || '');

interface ChartData {
  level1Data: Map<string, number>;
  level2Data: Map<string, number>;
  level3Data: Map<string, number>;
  selectedLevel1: string | null;
  selectedLevel2: string | null;
  currentLevel: number;
}

interface CachedAnalysis {
  result: string;
  timestamp: number;
  dataHash: string;
}

type StreamCallback = (chunk: string, isComplete: boolean) => void;

export class GeminiService {
  private model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
  private cache = new Map<string, CachedAnalysis>();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  async analyzeChartData(chartData: ChartData): Promise<string> {
    try {
      // Prepare data for analysis
      const analysisData = this.prepareDataForAnalysis(chartData);
      const dataHash = this.generateDataHash(analysisData);
      
      // Check cache first
      const cachedResult = this.getCachedResult(dataHash);
      if (cachedResult) {
        console.log('Using cached analysis result');
        return cachedResult;
      }
      
      // Create comprehensive prompt
      const prompt = this.createAnalysisPrompt(analysisData);
      
      // Get analysis from Gemini
      const result = await this.model.generateContent(prompt);
      const response = result.response;
      const analysisResult = response.text();
      
      // Cache the result
      this.cacheResult(dataHash, analysisResult);
      
      return analysisResult;
    } catch (error) {
      console.error('Gemini analysis error:', error);
      throw new Error('AI 분석 중 오류가 발생했습니다. API 키를 확인해주세요.');
    }
  }

  async analyzeChartDataStreaming(chartData: ChartData, onStream: StreamCallback): Promise<void> {
    try {
      // Prepare data for analysis
      const analysisData = this.prepareDataForAnalysis(chartData);
      const dataHash = this.generateDataHash(analysisData);
      
      // Check cache first
      const cachedResult = this.getCachedResult(dataHash);
      if (cachedResult) {
        console.log('Using cached analysis result with simulated streaming');
        // Simulate streaming for cached results
        this.simulateStreamingFromCache(cachedResult, onStream);
        return;
      }
      
      // Create comprehensive prompt
      const prompt = this.createAnalysisPrompt(analysisData);
      
      // Get streaming analysis from Gemini
      const result = await this.model.generateContentStream(prompt);
      let fullResponse = '';
      
      for await (const chunk of result.stream) {
        const chunkText = chunk.text();
        fullResponse += chunkText;
        onStream(chunkText, false);
      }
      
      // Cache the complete result
      this.cacheResult(dataHash, fullResponse);
      onStream('', true); // Signal completion
      
    } catch (error) {
      console.error('Gemini streaming analysis error:', error);
      throw new Error('AI 분석 중 오류가 발생했습니다. API 키를 확인해주세요.');
    }
  }

  private simulateStreamingFromCache(cachedResult: string, onStream: StreamCallback): void {
    const words = cachedResult.split(' ');
    let currentIndex = 0;
    
    const streamInterval = setInterval(() => {
      if (currentIndex >= words.length) {
        clearInterval(streamInterval);
        onStream('', true); // Signal completion
        return;
      }
      
      const wordsToAdd = Math.min(3, words.length - currentIndex); // Stream 3 words at a time
      const chunk = words.slice(currentIndex, currentIndex + wordsToAdd).join(' ') + ' ';
      onStream(chunk, false);
      currentIndex += wordsToAdd;
    }, 50); // 50ms between chunks for smooth streaming effect
  }

  private prepareDataForAnalysis(chartData: ChartData) {
    const level1Array = Array.from(chartData.level1Data.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({ category, count }));

    const level2Array = Array.from(chartData.level2Data.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({ category, count }));

    const level3Array = Array.from(chartData.level3Data.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({ category, count }));

    return {
      level1: level1Array,
      level2: level2Array,
      level3: level3Array,
      selectedLevel1: chartData.selectedLevel1,
      selectedLevel2: chartData.selectedLevel2,
      currentLevel: chartData.currentLevel,
      totalBooks: level1Array.reduce((sum, item) => sum + item.count, 0)
    };
  }

  private createAnalysisPrompt(data: any): string {
    const currentDate = new Date().toLocaleDateString('ko-KR');
    
    return `
**서울시 도서관 대출 데이터 분석 요청**

분석 날짜: ${currentDate}
총 대출 도서 수: ${data.totalBooks}권
현재 탐색 레벨: ${data.currentLevel}/3
선택된 주요 분류: ${data.selectedLevel1 || '없음'}
선택된 세부 분류: ${data.selectedLevel2 || '없음'}

**데이터 구조:**

1. 주요 분류 (Level 1):
${data.level1.map((item: any) => `   - ${item.category}: ${item.count}권 (${((item.count / data.totalBooks) * 100).toFixed(1)}%)`).join('\n')}

2. 세부 분류 (Level 2):
${data.level2.length > 0 ? data.level2.map((item: any) => `   - ${item.category}: ${item.count}권`).join('\n') : '   선택된 주요 분류가 없습니다.'}

3. 상세 분류 (Level 3):
${data.level3.length > 0 ? data.level3.map((item: any) => `   - ${item.category}: ${item.count}권`).join('\n') : '   선택된 세부 분류가 없습니다.'}

**분석 요청사항:**
한국어로 다음 내용을 Markdown 형식으로 분석해주세요:

## 1. 주요 트렌드 분석
가장 인기 있는 도서 분야와 그 이유를 분석해주세요.

## 2. 분포 패턴 분석
각 분류별 대출 비율의 특징과 패턴을 설명해주세요.

## 3. 주요 인사이트
데이터에서 발견할 수 있는 흥미로운 패턴이나 특징을 제시해주세요.

## 4. 독서 트렌드 분석
이 데이터가 시사하는 독서 트렌드와 사용자 행동을 분석해주세요.

## 5. 추천사항
도서관 운영이나 장서 개발에 대한 구체적인 제안을 제공해주세요.

각 섹션은 2-3문장으로 간결하게 작성하되, 전문적이면서도 일반 사용자가 이해하기 쉽게 작성해주세요.
중요한 수치나 비율은 **볼드체**로 강조해주세요.
`;
  }

  private generateDataHash(data: any): string {
    // Create a simple hash of the data for caching
    const jsonString = JSON.stringify(data, (_key, value) => {
      if (value instanceof Map) {
        return Array.from(value.entries());
      }
      return value;
    });
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString();
  }

  private getCachedResult(dataHash: string): string | null {
    const cached = this.cache.get(dataHash);
    if (!cached) return null;
    
    // Check if cache is still valid (within 24 hours)
    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_DURATION) {
      this.cache.delete(dataHash);
      return null;
    }
    
    return cached.result;
  }

  private cacheResult(dataHash: string, result: string): void {
    this.cache.set(dataHash, {
      result,
      timestamp: Date.now(),
      dataHash
    });
    
    // Clean up old cache entries (keep only last 50)
    if (this.cache.size > 50) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
      
      this.cache.clear();
      entries.slice(0, 50).forEach(([key, value]) => {
        this.cache.set(key, value);
      });
    }
  }

  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  clearCache(): void {
    this.cache.clear();
    console.log('Gemini analysis cache cleared');
  }
}

export const geminiService = new GeminiService();